import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const TERMS = {
  spread: {
    text: 'The predicted margin of victory. A -5.5 spread means the team is expected to win by about 6 points.',
    anchor: 'how-spreads-point-handicaps-work',
  },
  moneyline: {
    text: 'A bet on which team wins outright. Negative odds (-150) mean the favorite, positive odds (+130) mean the underdog.',
    anchor: 'what-is-a-moneyline',
  },
  overunder: {
    text: 'A bet on whether the total combined score will be over or under a set number.',
    anchor: 'overunder-totals-explained',
  },
  ats: {
    text: 'Against the Spread. Tracks how often a team beats the point spread, not just wins the game.',
    anchor: 'ats-records-and-why-they-matter',
  },
  cover: {
    text: "A team 'covers' when they beat the spread. If they're -7 and win by 8+, they covered.",
    anchor: 'what-covering-the-spread-means',
  },
  linemovement: {
    text: 'When odds change from their opening number. Movement can signal sharp action or new information.',
    anchor: 'line-movement-and-what-causes-it',
  },
  closingline: {
    text: 'The final odds just before a game starts. Considered the most accurate line.',
    anchor: 'closing-line-value-clv',
  },
  value: {
    text: 'When the odds offer a better payout than the true probability suggests.',
    anchor: 'how-to-spot-value-in-odds',
  },
  steam: {
    text: 'When multiple bookmakers move their line in the same direction at the same time, usually indicating sharp bettors.',
    anchor: 'steam-moves-and-reverse-line-movement',
  },
  rlm: {
    text: 'When the line moves opposite to where most public bets are going, suggesting sharp money on the other side.',
    anchor: 'steam-moves-and-reverse-line-movement',
  },
  impliedprob: {
    text: 'The win percentage that the odds suggest. -200 implies roughly 66.7% chance of winning.',
    anchor: 'implied-probability',
  },
  consensus: {
    text: 'The average or median line across all bookmakers.',
    anchor: 'line-shopping-across-bookmakers',
  },
};

export default function BettingTooltip({ term, children, inline = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState('below');
  const wrapperRef = useRef(null);
  const tipRef = useRef(null);
  const timeoutRef = useRef(null);

  const def = TERMS[term];
  if (!def) return children;

  function show() {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  }

  function hide() {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  }

  function toggle(e) {
    e.stopPropagation();
    setOpen((o) => !o);
  }

  // Reposition if tooltip overflows viewport
  useEffect(() => {
    if (!open || !tipRef.current) return;
    const rect = tipRef.current.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - 8) setPos('above');
    else setPos('below');
  }, [open]);

  // Close on outside click (mobile)
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onClickOutside);
    return () => document.removeEventListener('pointerdown', onClickOutside);
  }, [open]);

  return (
    <span
      ref={wrapperRef}
      className={`relative ${inline ? 'inline' : 'inline-flex items-center gap-0.5'}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      <button
        onClick={toggle}
        className="inline-flex items-center justify-center w-[13px] h-[13px] rounded-full text-[8px] font-bold leading-none text-[#8892a8] hover:text-[#3b82f6] border border-[#d1d5db] dark:border-[#2a3344] hover:border-[#3b82f6] transition-colors duration-150 cursor-help ml-0.5 flex-shrink-0"
        aria-label={`Info: ${term}`}
      >
        i
      </button>

      {open && (
        <div
          ref={tipRef}
          className={`absolute z-[100] rounded border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] shadow-lg px-3 py-2 left-0 ${
            pos === 'above' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
          style={{ width: 260, maxWidth: 260, minWidth: 260, whiteSpace: 'normal', overflowWrap: 'break-word' }}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <p className="text-[11px] leading-relaxed text-[#475569] dark:text-[#8892a8] mb-1.5">{def.text}</p>
          <Link
            to="/learn"
            className="text-[10px] text-[#3b82f6] hover:text-[#2563eb] font-medium transition-colors duration-150"
            onClick={() => setOpen(false)}
          >
            Learn more &rarr;
          </Link>
        </div>
      )}
    </span>
  );
}
